'use client';

import type { ReactNode } from 'react';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';

type ResourceSliderProps = {
  label: string;
  icon?: ReactNode;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  disabled?: boolean;
  /** Small badge next to the label (e.g. "Admin override") */
  badge?: ReactNode;
  className?: string;
};

// The one resource slider used on the pricing page, home page, create page and the plan tab
// so RAM / storage / CPU controls look identical everywhere.
export default function ResourceSlider({
  label, icon, value, onChange, min, max, step = 1, unit, disabled, badge, className,
}: ResourceSliderProps) {
  const locked = disabled || min >= max;
  const safeMax = Math.max(max, min + step);
  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2 text-sm font-medium">
          {icon ? <span className="text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">{icon}</span> : null}
          <span className="truncate">{label}</span>
          {badge}
        </div>
        <span className="shrink-0 rounded-md border border-border bg-muted/60 px-2.5 py-1 text-sm font-semibold tabular-nums">
          {value}{unit ? ` ${unit}` : ''}
        </span>
      </div>
      <Slider
        value={[value]}
        onValueChange={(v) => onChange(v[0] ?? value)}
        min={min}
        max={safeMax}
        step={step}
        disabled={locked}
        className={cn(locked && 'opacity-60')}
        aria-label={label}
      />
      <div className="flex justify-between text-xs tabular-nums text-muted-foreground">
        <span>{min}{unit ? ` ${unit}` : ''}</span>
        <span>{max}{unit ? ` ${unit}` : ''}</span>
      </div>
    </div>
  );
}
